import React from 'react';
import { Link, Tooltip } from '@mui/material';
import { decimalsToWei, LiquidityAmount } from '@akropolis-web/primitives';

import { styled, colors } from 'core/styles';
import { getAmount } from 'domain/utils';
import { APOSTRO_GLOSSARY_UB_MODEL_URL, APOSTRO_GLOSSARY_UMI_MODEL_URL } from 'env';
import { combine, left, MaybeEither, toEither } from 'utils/either';
import { ERROR_MESSAGES } from 'domain/utils/constants';
import { TagBox } from 'components/TagBox';

import { FormattedAmount } from '../FormattedAmount';

const maxImpactCost = getAmount(decimalsToWei(18), '$').mul(1000000000);

type TagContent = { tag: string; title: () => JSX.Element; description: React.ReactNode };

const upperBoundTagContent: TagContent = {
  tag: 'UB',
  title: () => (
    <span>
      Based on{' '}
      <LinkInTooltip href={APOSTRO_GLOSSARY_UB_MODEL_URL} target="_blank">
        Upper Bound model
      </LinkInTooltip>
    </span>
  ),
  description: (
    <span>
      Our estimate of oracle manipulation cost depends on the current market regime, token&apos;s
      liquidity and market capitalization.
    </span>
  ),
};

const tagContentByModel: Partial<Record<string, TagContent>> = {
  UPPER_BOUND_V2: upperBoundTagContent,
  UPPER_BOUND: upperBoundTagContent,
  UNISWAP: {
    tag: 'UNI',
    title: () => (
      <span>
        Based on{' '}
        <LinkInTooltip href={APOSTRO_GLOSSARY_UMI_MODEL_URL} target="_blank">
          Uniswap V3 model
        </LinkInTooltip>
      </span>
    ),
    description: (
      <span>
        Algorithm calculates the cost of short-term (5-10 minute timeframe) TWAP manipulation on
        Uniswap V3 using a single market order
      </span>
    ),
  },
};

export type ImpactCostProps = {
  cost: MaybeEither<LiquidityAmount>;
  model: MaybeEither<string>;
  reserveTagSpace?: boolean;
};

export function ImpactCost(props: ImpactCostProps) {
  const { reserveTagSpace = false } = props;

  return combine(toEither(props.cost), toEither(props.model)).fold(
    ([cost, model]) => {
      const displayMax = cost.gt(maxImpactCost);
      return (
        <Tooltip
          arrow={false}
          title={<ImpactCostHint cost={cost} model={model} />}
          enterTouchDelay={0}
          leaveTouchDelay={4000}
        >
          <ImpactCostBox>
            <span>
              {displayMax && <>{'>'}&nbsp;</>}
              <FormattedAmount
                sum={displayMax ? maxImpactCost : cost}
                precision={2}
                symbolSize="inherit"
                withSI
                siPrecision="million"
                tooltip="none"
              />
            </span>
            <ImpactCostTag model={model} reserveTagSpace={reserveTagSpace} />
          </ImpactCostBox>
        </Tooltip>
      );
    },
    () => (
      <ImpactCostBox>
        <FormattedAmount sum={left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA)} />
        <ImpactCostTag model="unknown" reserveTagSpace={reserveTagSpace} />
      </ImpactCostBox>
    ),
  );
}

function ImpactCostTag(props: { model: string; reserveTagSpace: boolean }) {
  const { model, reserveTagSpace } = props;

  const content = tagContentByModel[model];

  if (!content) {
    return reserveTagSpace ? <TagBox sx={{ visibility: 'hidden' }} /> : null;
  }

  const { tag } = content;

  return <TagBox>{tag}</TagBox>;
}

function ImpactCostHint(props: { cost: LiquidityAmount; model: string }) {
  const { cost, model } = props;
  const description = tagContentByModel[model]?.description;
  const Title = tagContentByModel[model]?.title;

  return (
    <>
      <div>{cost.toFormattedString(2)}</div>
      {Title && (
        <HintTitle>
          <Title />
        </HintTitle>
      )}
      {description && <HintDescription>{description}</HintDescription>}
    </>
  );
}

const ImpactCostBox = styled('div')(() => ({
  display: 'inline-flex',
  gap: '0 10px',
}));

const LinkInTooltip = styled(Link)(() => ({
  color: colors.black,
  textDecoration: 'underline',
  transition: 'opacity 200ms',

  '&:hover': {
    opacity: 0.7,
  },
}));

const HintTitle = styled('div')(() => ({
  fontWeight: 'bold',
}));

const HintDescription = styled('div')(() => ({
  color: colors.blackOpacity[40],
}));
